import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, EditOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import type { EquivalencyGroup } from "@/graphql/codegen/graphql"

interface EquivalentProductGroupRowActionsProps {
  row: Row<EquivalencyGroup>
  onEdit?: (group: EquivalencyGroup) => void
  onDelete?: (equivalencyGroupId: string, groupName: string) => Promise<boolean>
}

const EquivalentProductGroupRowActions: FC<EquivalentProductGroupRowActionsProps> = ({ row, onEdit, onDelete }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const anchorElRef = useRef<HTMLButtonElement>(null)

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsMenuOpen(true)
  }

  const handleMenuClose = () => {
    setIsMenuOpen(false)
  }

  const handleEdit = () => {
    handleMenuClose()
    onEdit?.(row.original)
  }

  const handleRemoveClick = () => {
    handleMenuClose()
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    const success = await onDelete?.(row.original.equivalencyGroupId, row.original.name)
    if (success) {
      setIsDeleteDialogOpen(false)
    }
  }

  return (
    <>
      <Box className='flex items-center justify-end'>
        <Box className='flex items-center justify-end' ref={anchorElRef}>
          <IconButton onClick={handleMenuOpen} data-testid='row-actions-button'>
            <MoreVertOutlined />
          </IconButton>
        </Box>
      </Box>

      <Menu anchorEl={anchorElRef.current} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit} data-testid='edit-menu-item'>
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          <ListItemText primary='Edit Group Information' />
        </MenuItem>
        <MenuItem onClick={handleRemoveClick} data-testid='remove-menu-item'>
          <ListItemIcon>
            <DeleteOutlined />
          </ListItemIcon>
          <ListItemText primary='Remove Group' />
        </MenuItem>
      </Menu>

      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title={`Are you sure you want to remove ${row.original.name}?`}
          content='This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          isWarning
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  )
}

export default EquivalentProductGroupRowActions
